import React, { useEffect } from 'react'

import {
  TextField,
  Grid,
  Box,
  Button,
  Typography,
  Link,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { reverse } from 'named-urls'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'

import { Card } from 'components'
import theme from 'theme'
import { routes } from 'Routes'

import schema from './schema'

import * as service from 'service'
import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'

const CommitteesForm = ({
  roles = [],
  availableUsers = [],
  formType = 'new',
  existingCommitteeUser = {},
  setLoading,
  setOpenDialogNewCommitteeRole,
  refresh = () => {},
}) => {
  const history = useHistory()
  const snackbar = useSnackbar()
  const params = useParams()

  const isNew = formType === 'new'

  const { control, handleSubmit, errors, reset, getValues } = useForm({
    validationSchema: schema({ isNew }),
    defaultValues: {
      user: null,
      committeeRole: null,
    },
  })

  useEffect(() => {
    if (!isNew && existingCommitteeUser) {
      const currentUser = getValues('user')

      if (currentUser?.id !== existingCommitteeUser.user?.id) {
        reset({
          user: existingCommitteeUser.user,
          committeeRole: existingCommitteeUser.committeeRole,
        })
      }
    }
  }, [existingCommitteeUser, isNew, reset, getValues])

  const handleReturn = () =>
    history.push(
      reverse(routes.committees.show, { committeeId: params.committeeId }),
    )

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      if (isNew) {
        const formattedData = {
          committeeRoleId: data?.committeeRole?.id,
          userId: data?.user?.id,
        }

        await service.dponet.committeeUsers.create({
          committeeId: params.committeeId,
          committeeUser: formattedData,
        })
        handleReturn()
      } else {
        await service.dponet.committeeUsers.changeRole({
          committeeId: params.committeeId,
          committeeUserId: existingCommitteeUser?.id,
          committeeRoleId: data?.committeeRole?.id,
        })
      }
      snackbar.open({
        message: `Usuário do comitê ${
          isNew ? 'adicionado' : 'atualizado'
        } com sucesso`,
        variant: 'success',
      })
      reset()
      refresh()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
      reset(data)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        display="flex"
        flexDirection="column"
        gridGap={theme.spacing(1)}
      >
        <Card
          title={
            isNew
              ? 'Selecione o usuário existente'
              : 'Membro existente selecionado'
          }
        >
          <Grid container spacing={2}>
            {isNew ? (
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="user"
                  mode="onChange"
                  onChange={([, data]) => data}
                  as={
                    <Autocomplete
                      id="combo-box-user"
                      options={availableUsers}
                      getOptionLabel={(option) => option.name}
                      getOptionSelected={(option, value) =>
                        option.id === value.id || value.id === 0
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Selecione o usuário"
                          error={!!errors.user}
                          helperText={errors?.user?.message}
                        />
                      )}
                    />
                  }
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  label="Usuário"
                  fullWidth
                  disabled
                  value={existingCommitteeUser?.user?.name || ''}
                />
              </Grid>
            )}
          </Grid>
        </Card>
        <Typography variant="h5" style={{ color: theme.palette.primary.main }}>
          Cargo no comitê
        </Typography>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="committeeRole"
            mode="onChange"
            onChange={([, data]) => data}
            as={
              <Autocomplete
                id="combo-box-role"
                options={roles}
                getOptionLabel={(option) => option.name}
                getOptionSelected={(option, value) =>
                  option.id === value.id || value.id === 0
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecione o cargo"
                    error={!!errors.committeeRole}
                    helperText={errors?.committeeRole?.message}
                    style={{
                      backgroundColor: theme.palette.background.paper,
                      marginBottom: '8px',
                    }}
                  />
                )}
              />
            }
          />
          <Typography variant="body2">
            Não encontrou a opção?{' '}
            <Link
              component="button"
              onClick={() => setOpenDialogNewCommitteeRole(true)}
              type="button"
              style={{ fontWeight: 'bold' }}
            >
              Clique aqui
            </Link>{' '}
            para adicionar
          </Typography>
        </Grid>
        <Box
          display="flex"
          justifyContent="flex-end"
          gridGap={theme.spacing(1)}
        >
          <Button variant="outlined" onClick={handleReturn}>
            Voltar
          </Button>
          <Button variant="contained" color="primary" type="submit">
            Salvar
          </Button>
        </Box>
      </Box>
    </>
  )
}

CommitteesForm.propTypes = {
  availableUsers: PropTypes.arrayOf(PropTypes.object),
  roles: PropTypes.arrayOf(PropTypes.object),
  formType: PropTypes.oneOf(['new', 'edit']).isRequired,
  setLoading: PropTypes.func,
}

export default CommitteesForm
